import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';

import { DeviceService } from 'src/app/shared/services/device/device.service';
import { HelmsdeepService } from 'src/app/shared/services/helmsdeep/helmsdeep.service';
import { RearLoadingStore } from 'src/app/shared/services/store/rear-loading/rear-loading.store';
import { Utils } from 'src/app/shared/utils/utils';
import { selectFrodoSettings } from 'src/app/stores/settings';

import { IbabelfishModel } from '../../interfaces/IbabelfishModel';
import { GuidedLoadingContext } from '../../interfaces/IGuidedLoadingContext';
import { ApiErrorHandlerService } from '../../services/api-error-handler/api-error-handler.service';
import { BabelfishService } from '../../services/babelfish/babelfish.service';
import { FRICTIONLESS_FLOW, MOBILE_TYPE } from '../../utils/constants';
import { getOnCancelRedirectPath } from '../../utils/guided-loading.util';

@Component({
  selector: 'frodo-processing-order',
  templateUrl: './processing-order.component.html',
  styleUrls: ['./processing-order.component.scss'],
})
export class ProcessingOrderComponent implements OnInit, OnDestroy {
  private routeData: GuidedLoadingContext;

  side = '';
  title = '';
  logo = './../../assets/logo.png';
  logoAlt = 'logo';
  colorSpinner = '#F8971D';
  src = './../../../../assets/timer.svg';
  enableOverflowArea = false;
  fileProgress = 0;
  frodoContext = '';
  flow = '';
  type = '';
  deviceSn = '';
  scheduleId = '';
  subscription: Subscription = new Subscription();

  constructor(
    private activatedRoute: ActivatedRoute,
    private utils: Utils,
    private store: Store,
    private helmsdeepService: HelmsdeepService,
    private apiErrorHandlerService: ApiErrorHandlerService,
    private babelfishService: BabelfishService,
    private deviceService: DeviceService,
    private rearLoadingStore: RearLoadingStore
  ) {}

  ngOnInit(): void {
    this.routeData = this.activatedRoute.snapshot.data as GuidedLoadingContext;
    const { frodoContext, side } = this.routeData;
    this.frodoContext = frodoContext;
    this.side = side;
    this.title = this.routeData.translations.processingOrderTitle;

    this.subscription.add(
      this.deviceService.getSerialNum.subscribe((serialNum) => {
        this.deviceSn = serialNum;
      })
    );
    this.subscription.add(
      this.store.select(selectFrodoSettings).subscribe((settings) => {
        this.flow = this.activatedRoute.snapshot.paramMap.get('flow');
        this.type = this.activatedRoute.snapshot.paramMap.get('type');
        this.enableOverflowArea =
          this.activatedRoute.snapshot.paramMap.get('enableOverflowArea') === 'true';
        this.scheduleId = this.activatedRoute.snapshot.paramMap.get('scheduleId');
        this.waitingToLoadOrder(settings.timeouts.confirmation);
      })
    );
  }

  waitingToLoadOrder(timeout: number): void {
    setTimeout(() => {
      this.flow === FRICTIONLESS_FLOW ? this.frictionlessFlow() : this.prescriptiveFlow();
    }, timeout);
  }

  frictionlessFlow(): void {
    const data: IbabelfishModel = {
      url: '/updateExternalApp',
      handlerId: 'complete',
      reqId: '1234',
      res: { scheduleId: this.scheduleId },
      body: JSON.stringify({
        payload: {
          deviceSn: this.deviceSn,
          side: this.side,
          scheduleId: this.scheduleId,
        },
      }),
    };
    this.babelfishService.babelfish(data).subscribe(
      () => {
        this.sendMsgAndNavigate();
      },
      (error) => {
        this.apiErrorHandlerService.handleApiError(error, '');
        this.cancel();
      }
    );
  }

  prescriptiveFlow(): void {
    if (this.enableOverflowArea) {
      this.subscription.add(
        this.helmsdeepService
          .loadOrder(this.scheduleId, this.side, this.enableOverflowArea)
          .subscribe(
            () => {
              this.sendMsgAndNavigate();
            },
            (error) => {
              this.apiErrorHandlerService.handleApiError(error, '');
              this.cancel();
            }
          )
      );
    } else {
      this.subscription.add(
        this.helmsdeepService.finishLoadOrder(this.scheduleId, this.side).subscribe(
          () => {
            this.sendMsgAndNavigate();
          },
          (error) => {
            this.apiErrorHandlerService.handleApiError(error, '');
            this.cancel();
          }
        )
      );
    }
  }

  sendMsgAndNavigate(): void {
    const { paths } = this.routeData;
    if (this.type === MOBILE_TYPE) {
      const schedule = JSON.parse(localStorage.getItem('schedule'));
      this.subscription.add(
        this.babelfishService.sendMessage(schedule.enduser, schedule.clientCode).subscribe(
          () => {},
          (error) => {
            this.apiErrorHandlerService.handleApiError(error, '');
          }
        )
      );
    }

    this.rearLoadingStore.updateSessionPositions([]);

    this.utils.redirect(`${this.frodoContext}/${paths.LOAD_CONFIRMATION}`, {
      type: this.type,
      flow: this.flow,
    });
  }

  getTargetScreen(): string {
    return getOnCancelRedirectPath(this.routeData.frodoContext, this.type);
  }

  cancel(): void {
    const { frodoContext } = this.routeData;
    this.utils.cancelLoading({
      frodoContext,
      targetScreen: this.getTargetScreen(),
      type: this.type,
      flow: this.flow,
      scheduleId: this.scheduleId,
    });
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
